/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Page } from 'src/core/components/Page';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisAppPageContent } from 'src/luis/appShell/appPage/components/LuisAppPageContent';
import { LuisAppPageSubHeader } from 'src/luis/appShell/appPage/components/LuisAppPageSubHeader';
import { LuisAppSidebar } from 'src/luis/appShell/appPage/components/LuisAppSidebar';
import { useInitializeConversationApp } from 'src/luis/modules/apps/hooks/useInitializeConversationApp';
import { VersionedAppId } from 'src/luis/types/VersionedAppId';

const { styleComponent } = getStylist('LuisAppPage');
const AppPageContainer = styleComponent<IStackProps>(Stack)('AppPageContainer', { overflow: 'hidden' });
const AppContentContainer = styleComponent<IStackProps>(Stack)('AppContentContainer', { overflow: 'hidden' });

export const LuisAppPage = observer((props: VersionedAppId) => {
    const { localize } = useLocalization();
    const isAppLoaded = useInitializeConversationApp({ appId: props.appId });

    return isAppLoaded ? (
        <AppPageContainer grow>
            <LuisAppPageSubHeader />

            <AppContentContainer horizontal grow>
                <Stack.Item grow>
                    <LuisAppSidebar />
                </Stack.Item>

                <LuisAppPageContent />
            </AppContentContainer>
        </AppPageContainer>
    ) : (
        <Page title={localize(globalMessages.loading)} pageName="LuisAppPage" size="container-fluid" animateIn={false}>
            <ShimmerFactory fullWidth includeHeaderShimmer shimmerCount={10} />
        </Page>
    );
});
